/**
 * Elektrik Kumanda Atölyesi — Web Audio API Tabanlı Mekanik Ses Sentezleyici
 * Harici dosya bağımlılığı olmadan, kontaktör çekme/bırakma, sigorta açma/kapama,
 * termik atma ve buton basma seslerini gerçekçi akustik rezonans ile üretir.
 */

class WorkshopSoundEngine {
  private ctx: AudioContext | null = null;
  private isMuted: boolean = false;

  private getContext(): AudioContext | null {
    if (this.isMuted) return null;
    if (typeof window === 'undefined') return null;

    if (!this.ctx) {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        try {
          this.ctx = new AudioCtx();
        } catch {
          this.ctx = null;
        }
      }
    }

    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }

    return this.ctx;
  }

  public setMuted(muted: boolean): void {
    this.isMuted = muted;
  }

  /**
   * Kontaktör Çekme Sesi (CLACK)
   * Elektromanyetik bobinin nüveyi çekmesi (darbe) + bakır kontakların çarpışması (çınlama)
   */
  public playContactorEnergize(): void {
    const ctx = this.getContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;

      // 1. Ağır Manyetik Çekirdek Darbesi (Düşük frekanslı tok darbe)
      const oscThud = ctx.createOscillator();
      const gainThud = ctx.createGain();
      oscThud.type = 'triangle';
      oscThud.frequency.setValueAtTime(140, now);
      oscThud.frequency.exponentialRampToValueAtTime(35, now + 0.06);

      gainThud.gain.setValueAtTime(0.55, now);
      gainThud.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

      oscThud.connect(gainThud);
      gainThud.connect(ctx.destination);

      oscThud.start(now);
      oscThud.stop(now + 0.08);

      // 2. Bakır / Gümüş Kontak Çarpma Sesi (Metalik yüksek frekanslı klik)
      const oscClick = ctx.createOscillator();
      const gainClick = ctx.createGain();
      oscClick.type = 'sine';
      oscClick.frequency.setValueAtTime(1100, now + 0.012);
      oscClick.frequency.exponentialRampToValueAtTime(320, now + 0.045);

      gainClick.gain.setValueAtTime(0, now);
      gainClick.gain.setValueAtTime(0.4, now + 0.012);
      gainClick.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

      oscClick.connect(gainClick);
      gainClick.connect(ctx.destination);

      oscClick.start(now + 0.012);
      oscClick.stop(now + 0.05);
    } catch {
      // AudioContext engellendiyse sessizce geç
    }
  }

  /**
   * Kontaktör Bırakma Sesi (Yay Geri Tepmesi)
   * Bobin enerjisi kesilince güçlü yayın armatürü geri fırlatma sesi
   */
  public playContactorRelease(): void {
    const ctx = this.getContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(820, now);
      osc.frequency.exponentialRampToValueAtTime(180, now + 0.04);

      gain.gain.setValueAtTime(0.28, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.045);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.045);
    } catch {}
  }

  /**
   * Otomatik Sigorta / Şalter Açma-Kapama Sesi (Mekanik Çıtırtı)
   */
  public playBreakerToggle(): void {
    const ctx = this.getContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'square';
      osc.frequency.setValueAtTime(550, now);
      osc.frequency.exponentialRampToValueAtTime(120, now + 0.035);

      gain.gain.setValueAtTime(0.25, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.038);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.038);
    } catch {}
  }

  /**
   * Termik Röle Açma (Trip) Sesi
   */
  public playThermalTrip(): void {
    const ctx = this.getContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(1400, now);
      osc.frequency.exponentialRampToValueAtTime(280, now + 0.06);

      gain.gain.setValueAtTime(0.35, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.07);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.07);
    } catch {}
  }

  /**
   * Buton Basma Sesi
   */
  public playButtonClick(): void {
    const ctx = this.getContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.exponentialRampToValueAtTime(200, now + 0.02);

      gain.gain.setValueAtTime(0.18, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.022);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.022);
    } catch {}
  }

  /**
   * Kısa Devre & Ark Kıvılcımı Alarm Sesi (Buzzer + Spark burst)
   */
  public playShortCircuitAlarm(): void {
    const ctx = this.getContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(2400, now);
      osc.frequency.setValueAtTime(1800, now + 0.05);
      osc.frequency.setValueAtTime(2400, now + 0.1);
      osc.frequency.exponentialRampToValueAtTime(100, now + 0.22);

      gain.gain.setValueAtTime(0.4, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.22);
    } catch {}
  }
}

export const workshopAudio = new WorkshopSoundEngine();
